import React, { useEffect, useState, useRef } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { Text, Button, useTheme, Card } from 'react-native-paper';
import { useLocalSearchParams, router, Stack } from 'expo-router';
import {
  getDriverInvitation,
  getUserDetails,
  getDriverWorkContext,
  type DriverWorkContext,
  acceptDriverInvitation,
  requestInvitationRenewal,
  DriverInvitationData,
  ExpiredInvitationDetail,
} from '../../../src/api/apiService';
import { useTranslation } from 'react-i18next';
import Logo from '../../../src/components/Logo';
import { LoadingScreen } from '../../../src/components/LoadingScreen';
import { useAuth } from '../../../src/context/AuthContext';

export default function InvitationAcceptPage() {
  const { token } = useLocalSearchParams<{ token: string }>();
  const { t } = useTranslation();
  const { t: workT } = useTranslation('workCompany');
  const tRef = useRef(t);
  tRef.current = t;
  const theme = useTheme();
  const { userToken } = useAuth();
  const authTokenRef = useRef(userToken);
  authTokenRef.current = userToken;
  const [recipient, setRecipient] = useState<{ id: number; email: string } | null>(null);
  const [workContext, setWorkContext] = useState<DriverWorkContext | null>(null);
  const [accepting, setAccepting] = useState(false);
  const acceptingRef = useRef(false);
  const [invitation, setInvitation] = useState<DriverInvitationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // #207 matrix C5: an expired invite gets its own explanatory state instead
  // of a generic dead-end error — "ask for a new invite" notifies the owner.
  const [expired, setExpired] = useState<ExpiredInvitationDetail | null>(null);
  const [renewing, setRenewing] = useState(false);
  const [renewalSent, setRenewalSent] = useState(false);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setError(tRef.current('invalidInvitationLink', 'Link de convite inválido.'));
      setLoading(false);
      return;
    }
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    (async () => {
      const response = await getDriverInvitation(token);
      if (response.ok) {
        setInvitation(response.data as DriverInvitationData);
      } else if ((response.data as any)?.code === 'invitation_expired') {
        const detail = response.data as ExpiredInvitationDetail;
        setExpired(detail);
        setRenewalSent(!!detail.renewal_requested);
      } else {
        const detail = (response.data as any)?.detail || tRef.current('invitationNotFound', 'Convite não encontrado ou expirado.');
        setError(detail);
      }
      setLoading(false);
    })();
  }, [token]);

  useEffect(() => {
    let current = true;
    setRecipient(null);
    setWorkContext(null);
    if (userToken) {
      void Promise.all([getUserDetails(), getDriverWorkContext()]).then(([response, context]) => {
        if (!current) return;
        if (response.ok && response.data?.id && response.data?.email
            && context.ok && context.data && typeof context.data.relationship_status === 'string') {
          setRecipient({ id: response.data.id, email: response.data.email });
          setWorkContext(context.data);
        } else {
          setError(tRef.current('invitationAcceptanceFailed'));
        }
      }).catch(() => {
        if (current) setError(tRef.current('invitationAcceptanceFailed'));
      });
    }
    return () => { current = false; };
  }, [userToken]);

  const handleAccept = async () => {
    if (!recipient || !workContext || !userToken || !token || acceptingRef.current
        || !['company', 'self'].includes(invitation?.license_payer || '')) return;
    acceptingRef.current = true;
    setAccepting(true);
    const consentAuth = userToken;
    try {
      // Recheck authenticated identity and displayed terms, never the addressed mailbox.
      const me = await getUserDetails();
      const fresh = await getDriverInvitation(token);
      const currentWork = await getDriverWorkContext();
      const terms = fresh.data as DriverInvitationData;
      if (authTokenRef.current !== consentAuth || !me.ok || me.data?.id !== recipient.id
          || me.data?.email !== recipient.email || !fresh.ok
          || terms.license_payer !== invitation?.license_payer
          || terms.company_nipc !== invitation?.company_nipc
          || terms.company_name !== invitation?.company_name
          || !currentWork.ok || !currentWork.data
          || currentWork.data.relationship_id !== workContext.relationship_id
          || currentWork.data.work_company?.id !== workContext.work_company?.id
          || currentWork.data.license.payer !== workContext.license.payer) {
        setError(tRef.current('invitationIdentityChanged'));
        return;
      }
      const companyChange = workContext.work_company
        && workContext.work_company.tax_identifier !== terms.company_nipc;
      const accepted = companyChange && workContext.relationship_id != null
        ? await acceptDriverInvitation(token, {
          confirm_company_change: true, expected_relationship_id: workContext.relationship_id,
        })
        : await acceptDriverInvitation(token);
      if (!accepted.ok) {
        setError(tRef.current('invitationAcceptanceFailed'));
        return;
      }
      // Existing root re-entry resolves authoritative post-acceptance state.
      router.replace('/');
    } catch {
      setError(tRef.current('invitationAcceptanceFailed'));
    } finally {
      acceptingRef.current = false;
      setAccepting(false);
    }
  };

  const handleRequestRenewal = async () => {
    if (!token) return;
    setRenewing(true);
    try {
      const response = await requestInvitationRenewal(token);
      if (response.ok) {
        setRenewalSent(true);
      } else {
        setError(tRef.current('invitationRenewalError', 'Não foi possível pedir um novo convite. Tente novamente.'));
      }
    } finally {
      setRenewing(false);
    }
  };

  const handleContinue = () => {
    if (!invitation) return;
    if (invitation.existing_user) {
      // Existing user: go to login and pass invite details
      router.push({
        pathname: '/',
        params: {
          invite_token: token,
          invite_email: invitation.email,
        },
      });
    } else {
      // New user: go to registration with pre-fill and token
      router.push({
        pathname: '/register/driver',
        params: {
          email: invitation.email,
          full_name: invitation.full_name,
          phoneNumber: invitation.phone_number,
          address: invitation.address,
          license_number: invitation.license_number,
          date_of_birth: invitation.date_of_birth,
          token,
        },
      });
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (expired) {
    return (
      <View style={[styles.center, { backgroundColor: theme.colors.background }]}>
        <Stack.Screen options={{ title: t('invitationExpiredTitle', 'Convite expirado') }} />
        <Card style={styles.card}>
          <Card.Content>
            <Text
              variant="titleMedium"
              style={{ color: theme.colors.error, textAlign: 'center', marginBottom: 12 }}
              accessibilityRole="header"
            >
              {t('invitationExpiredTitle', 'Convite expirado')}
            </Text>
            <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant, textAlign: 'center', marginBottom: 20 }}>
              {t(
                'invitationExpiredMessage',
                'O convite de {{company}} para {{email}} já não é válido — convites duram 7 dias.',
                { company: expired.company_name, email: expired.email }
              )}
            </Text>
            {renewalSent ? (
              <Text
                variant="bodyMedium"
                style={{ color: theme.colors.primary, textAlign: 'center' }}
                testID="renewal-requested-confirmation"
                accessibilityLiveRegion="polite"
              >
                {t('invitationRenewalRequested', 'Pedido enviado — {{company}} foi avisada para lhe enviar um novo convite.', { company: expired.company_name })}
              </Text>
            ) : (
              <Button
                mode="contained"
                onPress={handleRequestRenewal}
                loading={renewing}
                disabled={renewing}
                testID="request-renewal-button"
              >
                {t('invitationRequestRenewal', 'Pedir novo convite')}
              </Button>
            )}
            <Button mode="text" onPress={() => router.replace('/')} style={{ marginTop: 12 }}>
              {t('goHome', 'Go to Home')}
            </Button>
          </Card.Content>
        </Card>
      </View>
    );
  }

  if (error) {
    return (
      <View style={[styles.center, { backgroundColor: theme.colors.background }]}> 
        <Card style={styles.card}>
          <Card.Content>
            <Text variant="titleMedium" style={{ color: theme.colors.error, textAlign: 'center' }}>{error}</Text>
            <Button mode="contained" onPress={() => router.replace('/')} style={{ marginTop: 16 }}>
              {t('goHome', 'Go to Home')}
            </Button>
          </Card.Content>
        </Card>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}> 
      <Stack.Screen options={{ title: t('acceptInvitation', 'Aceitar Convite') }} />
      <Logo size={Platform.OS === 'web' ? 'medium' : 'mobile'} />
      <Card style={styles.card}>
        <Card.Content>
          <Text variant="headlineMedium" style={{ color: theme.colors.primary, textAlign: 'center', marginBottom: 12 }}>
            {t('invitationTitle', { company: invitation?.company_name })}
          </Text>
          <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant, textAlign: 'center', marginBottom: 24 }}>
            {invitation?.existing_user
              ? t('invitationLoginMessage', 'Faça login para aceitar o convite')
              : t('invitationRegistrationMessage', 'Complete o cadastro para aceitar o convite')}
          </Text>
          <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
            {t(invitation?.license_payer === 'company' ? 'invitationPayerCompany'
              : invitation?.license_payer === 'self' ? 'invitationPayerSelf' : 'invitationPayerUnknown')}
          </Text>
          {userToken && recipient && workContext && <>
            <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
              {t('invitationRecipient', { email: recipient.email })}
            </Text>
            <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant, marginTop: 12 }}>
              {t('invitationCompanyConsent')}
            </Text>
            {workContext.work_company && workContext.work_company.tax_identifier !== invitation?.company_nipc &&
              <Text variant="bodyMedium" style={{ marginTop: 12 }}>{workT('invitationChange', {
                current: workContext.work_company.name, destination: invitation?.company_name,
              })}</Text>}
            {invitation?.license_payer === 'company' && <Text variant="bodyMedium" style={{ marginTop: 12 }}>
              {workT('invitationPaidPeriod')}
            </Text>}
            {['company', 'self'].includes(invitation?.license_payer || '') &&
              <Button mode="contained" onPress={handleAccept} loading={accepting} disabled={accepting} style={styles.button}>
                {t('invitationConfirm')}
              </Button>}
          </>}
          {!userToken && <Button mode="contained" onPress={handleContinue} style={styles.button}>
            {invitation?.existing_user ? t('login', 'Login') : t('continue', 'Continuar')}
          </Button>}
        </Card.Content>
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  container: {
    flex: 1,
    padding: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 560,
    alignSelf: 'center',
  },
  title: {
    fontSize: 24,
    marginBottom: 12,
    textAlign: 'center',
  },
  message: {
    fontSize: 16,
    marginBottom: 24,
    textAlign: 'center',
  },
  button: {
    marginTop: 12,
  },
}); 